import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { ChevronDown, ChevronRight, X, SlidersHorizontal } from 'lucide-react';
import type { Facets, FacetBucket, SearchFilters, FilterFacetConfig } from '../utils/types';
import { getPublicFilterFacets } from '../utils/api';
import { PriceRangeFilter } from './PriceRangeFilter';
import { useI18n } from '../i18n/I18nContext';
import { translateFacetLabel } from '../i18n/index';
import { useFontSizes } from '../contexts/FontSizeContext';

interface FacetSidebarProps {
  facets?: Facets;
  filters: SearchFilters;
  onChangeFilters: (filters: SearchFilters) => void;
  isVisible: boolean;
  onToggleVisibility: () => void;
}

interface FacetSectionProps {
  title: string;
  titleEn?: string;
  buckets: FacetBucket[];
  selectedValue?: string;
  onSelect: (value: string | undefined) => void;
  defaultExpanded?: boolean;
  maxVisible?: number;
  bodyClass: string;
  smallClass: string;
}

const FacetSection = memo<FacetSectionProps>(
  ({ title, buckets, selectedValue, onSelect, defaultExpanded = true, maxVisible = 5, bodyClass, smallClass }) => {
    const { t } = useI18n();
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
    const [showAll, setShowAll] = useState(false);

    const visibleBuckets = showAll ? buckets : buckets.slice(0, maxVisible);
    const hasMore = buckets.length > maxVisible;

    if (buckets.length === 0) return null;

    return (
      <div className="border-b border-gray-100 last:border-b-0">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between py-2.5 px-3 hover:bg-gray-50 transition-colors text-left"
        >
          <span className={`${bodyClass} font-medium text-gray-700`}>{title}</span>
          {isExpanded ? (
            <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
          )}
        </button>

        {isExpanded && (
          <div className="px-3 pb-2.5 space-y-1">
            {visibleBuckets.map((bucket) => {
              const isSelected = selectedValue === bucket.value;
              return (
                <button
                  key={bucket.value}
                  type="button"
                  onClick={() => onSelect(isSelected ? undefined : bucket.value)}
                  className={[
                    'w-full flex items-center justify-between py-1 px-2 rounded text-left',
                    `transition-colors ${bodyClass}`,
                    isSelected
                      ? 'bg-wine/10 text-wine'
                      : 'hover:bg-gray-50 text-gray-600',
                  ].join(' ')}
                >
                  <span className="truncate pr-2">{bucket.label}</span>
                  <span
                    className={[
                      `${smallClass} flex-shrink-0`,
                      isSelected ? 'text-wine/70' : 'text-gray-400',
                    ].join(' ')}
                  >
                    {bucket.count}
                  </span>
                </button>
              );
            })}

            {hasMore && (
              <button
                type="button"
                onClick={() => setShowAll(!showAll)}
                className={`${smallClass} text-wine hover:text-wine-dark px-2 py-1`}
              >
                {showAll ? t('filter.showLess') : t('filter.showMore', { count: buckets.length - maxVisible })}
              </button>
            )}
          </div>
        )}
      </div>
    );
  }
);
FacetSection.displayName = 'FacetSection';

/** Maps facet response key → { filter field name, i18n title key } */
const FACET_META: Record<string, { filterField: string; titleKey: string }> = {
  categories:    { filterField: 'category',    titleKey: 'filter.category' },
  subcategories: { filterField: 'subcategory', titleKey: 'filter.subcategory' },
  wineTypes:     { filterField: 'type',        titleKey: 'filter.wineType' },
  countries:     { filterField: 'country',     titleKey: 'filter.country' },
  regions:       { filterField: 'region',      titleKey: 'filter.region' },
  grapes:        { filterField: 'grape',       titleKey: 'filter.grape' },
  producers:     { filterField: 'producer',    titleKey: 'filter.producer' },
  vintages:      { filterField: 'vintage',     titleKey: 'filter.vintage' },
  tags:          { filterField: 'tags',        titleKey: 'filter.tags' },
  bodies:        { filterField: 'body',        titleKey: 'filter.body' },
  acidities:     { filterField: 'acidity',     titleKey: 'filter.acidity' },
  tannins:       { filterField: 'tannins',     titleKey: 'filter.tannins' },
  fruitiness:    { filterField: 'fruitiness',  titleKey: 'filter.fruitiness' },
};

export const FacetSidebar: React.FC<FacetSidebarProps> = ({
  facets,
  filters,
  onChangeFilters,
  isVisible,
  onToggleVisibility,
}) => {
  const { t, lang } = useI18n();
  const { heading, body, small } = useFontSizes();
  const hasActiveFilters = Object.entries(filters).some(
    ([k, v]) => v !== undefined && k !== 'sort',
  );

  // Honor the admin "Categories & Filters" config: hide facets explicitly
  // disabled there, and order the rest by the configured order. Facets the
  // admin hasn't configured still show (after configured ones), so adding data
  // never silently drops a filter.
  const [facetConfig, setFacetConfig] = useState<FilterFacetConfig[]>([]);
  useEffect(() => {
    let alive = true;
    getPublicFilterFacets(lang).then((c) => { if (alive) setFacetConfig(c); }).catch(() => {});
    return () => { alive = false; };
  }, [lang]);
  const disabledFields = useMemo(
    () => new Set(facetConfig.filter((c) => !c.enabled).map((c) => c.field)),
    [facetConfig],
  );
  const orderByField = useMemo(() => {
    const m = new Map<string, number>();
    facetConfig.forEach((c) => m.set(c.field, c.order));
    return m;
  }, [facetConfig]);

  const clearAllFilters = useCallback(() => {
    onChangeFilters({});
  }, [onChangeFilters]);

  const handleFacetSelect = useCallback(
    (field: string, value: string | undefined) => {
      onChangeFilters({ ...filters, [field]: value });
    },
    [filters, onChangeFilters]
  );

  const handlePriceChange = useCallback(
    (min?: number, max?: number) => {
      onChangeFilters({ ...filters, minPrice: min, maxPrice: max });
    },
    [filters, onChangeFilters]
  );

  const handleInStockToggle = useCallback(() => {
    onChangeFilters({ ...filters, inStock: filters.inStock ? undefined : true });
  }, [filters, onChangeFilters]);

  // Toggle button shown when sidebar is hidden
  if (!isVisible) {
    return (
      <button
        type="button"
        onClick={onToggleVisibility}
        className={[
          'fixed left-4 top-1/2 -translate-y-1/2 z-50',
          'p-2 rounded-full bg-white shadow-lg border border-gray-200',
          'hover:bg-gray-50 transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-wine/20',
        ].join(' ')}
        aria-label={t('filter.showFilters')}
      >
        <SlidersHorizontal className="h-4 w-4 text-gray-600" />
        {hasActiveFilters && (
          <span className="absolute -top-1 -right-1 w-2 h-2 bg-wine rounded-full" />
        )}
      </button>
    );
  }

  const priceMin = facets?.priceRange?.min ?? 0;
  const priceMax = facets?.priceRange?.max ?? 1000;

  return (
    <div className="w-full bg-white border-r border-gray-100 overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-100 sticky top-0 bg-white z-10">
        <div className="flex items-center gap-1.5">
          <SlidersHorizontal className="h-3.5 w-3.5 text-gray-500" />
          <span className={`${heading} font-medium text-gray-700`}>{t('filter.label')}</span>
        </div>
        <div className="flex items-center gap-1">
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearAllFilters}
              className={`${small} text-wine hover:text-wine-dark px-1.5 py-0.5`}
            >
              {t('filter.clearAll')}
            </button>
          )}
          <button
            type="button"
            onClick={onToggleVisibility}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            aria-label={t('filter.hideFilters')}
          >
            <X className="h-3.5 w-3.5 text-gray-400" />
          </button>
        </div>
      </div>

      {/* Price Range */}
      <div className="border-b border-gray-100 px-3 py-2.5">
        <span className={`${body} font-medium text-gray-700 block mb-2`}>{t('filter.price')}</span>
        <PriceRangeFilter
          min={filters.minPrice}
          max={filters.maxPrice}
          rangeMin={priceMin}
          rangeMax={priceMax}
          onChange={handlePriceChange}
        />
      </div>

      {/* In Stock Toggle */}
      <div className="border-b border-gray-100 px-3 py-2.5">
        <button
          type="button"
          onClick={handleInStockToggle}
          className={[
            `w-full flex items-center justify-between py-1.5 px-2 rounded ${body}`,
            'transition-colors',
            filters.inStock
              ? 'bg-green-50 text-green-700'
              : 'hover:bg-gray-50 text-gray-600',
          ].join(' ')}
        >
          <span>{t('filter.inStockOnly')}</span>
          <span
            className={[
              'w-4 h-4 rounded border flex items-center justify-center',
              filters.inStock
                ? 'bg-green-500 border-green-500 text-white'
                : 'border-gray-300',
            ].join(' ')}
          >
            {filters.inStock && (
              <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 12 12">
                <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
              </svg>
            )}
          </span>
        </button>
      </div>

      {/* Dynamic Facet Sections — rendered for each key with data, honoring the
          admin facet config (disabled hidden, configured order first). */}
      {facets && Object.entries(facets)
        .filter(([facetKey, buckets]) => {
          if (facetKey === 'priceRange' || !Array.isArray(buckets) || buckets.length === 0) return false;
          const meta = FACET_META[facetKey];
          return !!meta && !disabledFields.has(meta.filterField);
        })
        .sort(([a], [b]) => {
          const oa = orderByField.get(FACET_META[a].filterField);
          const ob = orderByField.get(FACET_META[b].filterField);
          if (oa == null && ob == null) return 0;
          if (oa == null) return 1; // unconfigured facets after configured ones
          if (ob == null) return -1;
          return oa - ob;
        })
        .map(([facetKey, buckets]) => {
        const meta = FACET_META[facetKey]!;
        const filterVal = (filters as Record<string, unknown>)[meta.filterField] as string | undefined;
        // Translate bucket labels for display (value stays Greek for filtering)
        const translatedBuckets = (buckets as FacetBucket[]).map((b) => ({
          ...b,
          label: translateFacetLabel(lang, b.label),
        }));
        return (
          <FacetSection
            key={facetKey}
            title={t(meta.titleKey as any)}
            buckets={translatedBuckets}
            selectedValue={filterVal}
            onSelect={(value) => handleFacetSelect(meta.filterField, value)}
            bodyClass={body}
            smallClass={small}
          />
        );
      })}
    </div>
  );
};
